import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button';
import { ELEVATION } from '@openx/components-core/lib/theme/elevation';
import { noop } from '@openx/utils/noop';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type DealTroubleshootingFeedbackFormProps = {
  onSubmit: (feedbackText: string) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting?: boolean;
};

export function DealTroubleshootingFeedbackForm({
  onSubmit,
  onCancel,
  isSubmitting = false,
}: DealTroubleshootingFeedbackFormProps) {
  const { t } = useTranslation();
  const [feedbackText, setFeedbackText] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  return (
    <Stack
      ref={containerRef}
      direction="column"
      sx={{
        gap: 1.5,
      }}
    >
      <Stack
        direction="column"
        sx={{
          gap: 0.25,
        }}
      >
        <Typography variant="h2" sx={{ fontSize: 16 }}>
          {t('What was the problem?')}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 15 }}>
          {t('Your feedback will help make our AI even better.')}
        </Typography>
      </Stack>
      <Box
        sx={theme => ({
          boxShadow: ELEVATION.elevation0,
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        })}
      >
        <TextField
          multiline
          fullWidth
          minRows={3}
          maxRows={6}
          placeholder={t("Tell us why you didn't like this response.")}
          aria-label={t("Tell us why you didn't like this response.")}
          value={feedbackText}
          onChange={event => setFeedbackText(event.target.value)}
          slotProps={{
            input: {
              sx: {
                border: 'none',
                ':not(.Mui-disabled)&:hover': { backgroundColor: 'background.paper' },
                '&:focus': { outline: 'none' },
                pt: 1.5,
                pb: 0.5,
                px: 2,
                lineHeight: '20px',
                fontSize: 15,
              },
            },
          }}
        />
        <Stack
          direction="row"
          sx={{
            gap: 1,
            justifyContent: 'flex-end',
            backgroundColor: 'background.paper',
            px: 1.5,
            pb: 1,
          }}
        >
          <Button variant="text" onClick={onCancel}>
            {t('Cancel')}
          </Button>
          <Button
            loading={isSubmitting}
            disabled={!feedbackText.trim()}
            variant="contained"
            color="primary"
            onClick={async () => {
              try {
                await onSubmit(feedbackText);
              } catch {
                noop();
              }
            }}
          >
            {t('Send feedback')}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
